import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, getToken, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, MoreVertical, Download, Eye, EyeOff, Trash2, FileText,
    Image as ImageIcon, ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";

const CATEGORIES = [
    "Quote", "Contract", "Survey Report", "System Design", "DNO Application",
    "DNO Approval", "MCS Certificate", "Electrical Certificate", "Warranty",
    "Manual", "Installation Photograph", "Handover Pack", "Customer Upload", "Other",
];

function humanSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function humanDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
}

/**
 * InstallerDocumentsSection — 1:1 with mobile `InstallerDocumentsSection.tsx`.
 * Collapsible; per-doc row menu (Download / Toggle visibility / Delete)
 * with delete confirmation. Upload sheet supports title + category + visibility + internal note.
 */
export default function InstallerDocumentsSection({ projectId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    // UAT corrective pass · Engineer sections initialise as collapsed to
    // match the Operations "collapsed-by-default" convention.
    const [expanded, setExpanded] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [menuFor, setMenuFor] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const menuRef = useRef(null);
    const token = getToken();

    const load = useCallback(async () => {
        setLoading(true);
        try { setDocs(await api.listDocuments(projectId)); }
        catch { /* best effort */ }
        setLoading(false);
    }, [projectId]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        function onDoc(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuFor(null); }
        if (menuFor) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [menuFor]);

    const openDoc = async (d) => {
        try {
            const { token: dt } = await api.documentDownloadToken(d.id);
            window.open(`${api.documentDownloadUrl(d.id)}?dt=${encodeURIComponent(dt)}`, "_blank");
        } catch { toast.error("Download failed"); }
    };

    const flipVisibility = async (d) => {
        const next = d.visibility === "customer_visible" ? "internal_only" : "customer_visible";
        setMenuFor(null);
        try {
            const updated = await api.updateDocument(d.id, { visibility: next });
            setDocs((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x));
            toast.success(next === "customer_visible" ? "Now visible to customer" : "Set to internal only");
        } catch { toast.error("Could not update visibility"); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.deleteDocument(confirmDelete.id);
            setDocs((prev) => prev.filter((x) => x.id !== confirmDelete.id));
            setConfirmDelete(null);
            toast.success("Document deleted");
        } catch { toast.error("Delete failed"); }
    };

    return (
        <section className="mt-6" data-testid="installer-docs-section">
            <div className="flex items-center justify-between mb-3">
                <button data-testid="docs-toggle" onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-[#131B2F] hover:text-[#3E9E2E] transition-colors">
                    {expanded ? <ChevronDown size={16} strokeWidth={1.75} /> : <ChevronRight size={16} strokeWidth={1.75} />}
                    <span className="text-lg font-medium">Documents{!expanded && ` (${docs.length})`}</span>
                </button>
                <button data-testid="docs-upload-btn" onClick={() => setUploadOpen(true)} className="pe-btn-primary text-sm h-9">
                    <Plus size={14} strokeWidth={2} /> Upload
                </button>
            </div>

            {expanded && (
                loading ? (
                    <div className="py-8 text-center text-sm text-[#4B5565]">Loading documents…</div>
                ) : docs.length === 0 ? (
                    <div className="py-8 text-center text-sm text-[#4B5565]">No documents yet.</div>
                ) : (
                    <div className="space-y-2" data-testid="installer-docs-list">
                        {docs.map((d) => {
                            const isImage = (d.file_type || "").startsWith("image/");
                            const isCustomerVisible = d.visibility === "customer_visible";
                            return (
                                <div key={d.id} className="pe-card p-3 flex items-center gap-3" data-testid={`ip-doc-${d.id}`}>
                                    <button onClick={() => openDoc(d)} data-testid={`ip-doc-open-${d.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                        <span className="w-9 h-9 rounded-[10px] bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center shrink-0">
                                            {isImage ? <ImageIcon size={16} strokeWidth={1.75} /> : <FileText size={16} strokeWidth={1.75} />}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-base text-[#131B2F] font-medium truncate">{d.title}</div>
                                            <div className="text-sm text-[#4B5565] mt-0.5 truncate">
                                                {d.category}{d.file_size ? ` · ${humanSize(d.file_size)}` : ""} · {humanDate(d.uploaded_at)}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                                    isCustomerVisible ? "bg-[#E6F1DE] text-[#2E7E22]" : "bg-[#E5E7EB] text-[#4B5565]"
                                                }`} data-testid={`ip-doc-visibility-${d.id}`}>
                                                    {isCustomerVisible ? <Eye size={9} /> : <EyeOff size={9} />}
                                                    {isCustomerVisible ? "Customer" : "Internal"}
                                                </span>
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF]">
                                                    {d.uploaded_by_name}
                                                </span>
                                                {d.internal_note && (
                                                    <span className="text-[10px] text-[#4B5565] italic truncate max-w-[220px]">"{d.internal_note}"</span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                    <div ref={menuFor?.id === d.id ? menuRef : null} className="relative">
                                        <button data-testid={`ip-doc-menu-${d.id}`} onClick={() => setMenuFor(menuFor?.id === d.id ? null : d)}
                                            className="w-9 h-9 rounded-full flex items-center justify-center text-[#4B5565] hover:bg-[#F5F5F7]">
                                            <MoreVertical size={16} strokeWidth={1.75} />
                                        </button>
                                        {menuFor?.id === d.id && (
                                            <div className="absolute top-10 right-0 z-10 w-56 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1">
                                                <MenuRow icon={Download} label="Download" onClick={() => { setMenuFor(null); openDoc(d); }} testid={`ip-doc-menu-download-${d.id}`} />
                                                {isCustomerVisible ? (
                                                    <MenuRow icon={EyeOff} label="Make internal only"    onClick={() => flipVisibility(d)} testid={`ip-doc-menu-hide-${d.id}`} />
                                                ) : (
                                                    <MenuRow icon={Eye}    label="Make customer visible" onClick={() => flipVisibility(d)} testid={`ip-doc-menu-show-${d.id}`} />
                                                )}
                                                <MenuRow icon={Trash2} label="Delete" danger onClick={() => { setMenuFor(null); setConfirmDelete(d); }} testid={`ip-doc-menu-delete-${d.id}`} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {uploadOpen && <UploadSheet projectId={projectId} onClose={() => setUploadOpen(false)} onUploaded={(d) => { setDocs((prev) => [d, ...prev]); setUploadOpen(false); toast.success("Uploaded"); }} />}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" data-testid="ip-doc-confirm-delete">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
                        <div className="flex items-center gap-2 text-[#E53E3E]">
                            <AlertTriangle size={22} strokeWidth={1.75} />
                            <span className="text-lg text-[#131B2F] font-medium">Delete document?</span>
                        </div>
                        <div className="text-sm text-[#4B5565] mt-2">
                            "{confirmDelete.title}" will be removed for both you and the customer. This cannot be undone.
                        </div>
                        <div className="mt-4 flex gap-2">
                            <button className="pe-btn-outline flex-1 justify-center h-11" onClick={() => setConfirmDelete(null)}>Cancel</button>
                            <button data-testid="ip-doc-confirm-delete-yes" onClick={doDelete} className="pe-btn-primary flex-1 justify-center h-11 !bg-[#E53E3E] hover:!bg-[#C53030]">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function MenuRow({ icon: Icon, label, onClick, danger, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${danger ? "text-[#E53E3E] hover:bg-[#FEE2E2]" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}>
            <Icon size={14} strokeWidth={1.75} /> {label}
        </button>
    );
}

function UploadSheet({ projectId, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("Other");
    const [visibility, setVisibility] = useState("internal_only");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const inputRef = useRef(null);

    const submit = async () => {
        setErr("");
        if (!file) { setErr("Please choose a file"); return; }
        setBusy(true);
        try {
            const doc = await api.uploadDocument(projectId, file, title || file.name, category, visibility, note);
            onUploaded(doc);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" data-testid="upload-sheet">
            <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="text-lg text-[#131B2F] font-medium">Upload document</div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">File (PDF or image, ≤ 10 MB)</label>
                        <input ref={inputRef} data-testid="upload-file" type="file" accept="application/pdf,image/*"
                            onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !title) setTitle(f.name); }}
                            className="w-full text-sm text-[#131B2F]" />
                    </div>
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Title</label>
                        <input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                    </div>
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Category</label>
                        <select data-testid="upload-category" value={category} onChange={(e) => setCategory(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Visibility</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button data-testid="upload-vis-customer" onClick={() => setVisibility("customer_visible")}
                                className={`h-11 rounded-md border text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${visibility === "customer_visible" ? "border-[#3E9E2E] bg-[#F1F8EC] text-[#2E7E22]" : "border-[#E5E7EB] bg-white text-[#4B5565]"}`}>
                                <Eye size={13} strokeWidth={1.75} /> Customer visible
                            </button>
                            <button data-testid="upload-vis-internal" onClick={() => setVisibility("internal_only")}
                                className={`h-11 rounded-md border text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${visibility === "internal_only" ? "border-[#3E9E2E] bg-[#F1F8EC] text-[#2E7E22]" : "border-[#E5E7EB] bg-white text-[#4B5565]"}`}>
                                <EyeOff size={13} strokeWidth={1.75} /> Internal only
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-[#4B5565] mb-1.5">Internal note (optional)</label>
                        <textarea data-testid="upload-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                            className="w-full rounded-md border border-[#E5E7EB] p-3 text-base outline-none focus:border-[#3E9E2E]" />
                    </div>
                    {!!err && <div className="text-sm text-[#E53E3E]">{err}</div>}
                </div>
                <div className="p-4 border-t border-[#E5E7EB] flex gap-2">
                    <button className="pe-btn-outline flex-1 justify-center h-11" onClick={onClose}>Cancel</button>
                    <button data-testid="upload-submit" onClick={submit} disabled={busy || !file}
                        className="pe-btn-primary flex-1 justify-center h-11 disabled:opacity-60">
                        {busy ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}
